// ============================================
// Chat Context Provider
// Provides chat state and functions to all chat components
// ============================================

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAbly, useChannel } from './hooks/useAbly';
import { API_URL, getAnyValidToken } from '../../utils/authUtils';

// Create context
const ChatContext = createContext(null);

// Notification sound (base64 encoded short beep)
const NOTIFICATION_SOUND_URL = 'data:audio/wav;base64,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';

// Custom hook to use chat context
export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

/**
 * Chat Provider Component
 * Wraps the app and provides chat functionality
 */
export const ChatProvider = ({ children, tenant = 'nacme', enabled = true }) => {
  // Connection state
  const { client, connectionState, isConnected, error: connectionError, reconnect } = useAbly(tenant, enabled);

  // UI state
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  // Channel state
  const [channels, setChannels] = useState({ joined: [], available: [] });
  const [currentChannel, setCurrentChannel] = useState(null);
  const [loadingChannels, setLoadingChannels] = useState(false);

  // Message state
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);

  // Unread counts
  const [unreadCounts, setUnreadCounts] = useState({});
  const [totalUnread, setTotalUnread] = useState(0);

  // Typing indicators
  const [typingUsers, setTypingUsers] = useState([]);

  // Thread reply state
  const [replyingTo, setReplyingTo] = useState(null);
  const [threadReplies, setThreadReplies] = useState({});  // { messageId: { replies: [], hasMore: false, loading: false } }
  const [threadReplyAlert, setThreadReplyAlert] = useState(null);  // { parentMessageId, senderName, content, id }
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);  // For visual pulse effect

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchFilters, setSearchFilters] = useState({ dateFrom: null, dateTo: null, senderType: null, senderId: null });
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTotalCount, setSearchTotalCount] = useState(0);
  const [searchHasMore, setSearchHasMore] = useState(false);

  // Current user info (extracted from token)
  const [currentUser, setCurrentUser] = useState(null);

  // Notification state
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);

  // Presence state (global - not per-channel)
  const [myPresenceStatus, setMyPresenceStatus] = useState('online');  // 'online', 'away', 'dnd', 'offline'
  const [globalPresence, setGlobalPresence] = useState({});  // { `${userType}:${userId}`: { status, displayName, userType, userId } }
  const lastActivityRef = useRef(Date.now());
  const awayTimeoutRef = useRef(null);
  const globalPresenceChannelRef = useRef(null);

  // Refs for real-time updates
  const currentChannelRef = useRef(currentChannel);
  const messagesRef = useRef(messages);
  const isOpenRef = useRef(isOpen);
  const isMinimizedRef = useRef(isMinimized);
  const notificationAudioRef = useRef(null);
  const channelSubscriptionsRef = useRef({});
  const switchChannelRef = useRef(null);
  const currentUserRef = useRef(currentUser);

  // Message cache - stores messages by channel ID for instant switching
  const messageCacheRef = useRef({});
  const hasMoreCacheRef = useRef({});

  useEffect(() => {
    currentChannelRef.current = currentChannel;
  }, [currentChannel]);

  useEffect(() => {
    messagesRef.current = messages;
    // Update cache when messages change
    if (currentChannelRef.current?.id) {
      messageCacheRef.current[currentChannelRef.current.id] = messages;
    }
  }, [messages]);

  // Keep refs updated for notification logic
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    currentUserRef.current = currentUser;
  }, [currentUser]);

  useEffect(() => {
    isMinimizedRef.current = isMinimized;
  }, [isMinimized]);

  // Initialize notification audio
  useEffect(() => {
    notificationAudioRef.current = new Audio(NOTIFICATION_SOUND_URL);
    notificationAudioRef.current.volume = 0.5;
  }, []);

  // Suppress Ably "Channel detached" errors from React error overlay
  // These are expected during navigation and cleanup - not actual errors
  useEffect(() => {
    const handleError = (event) => {
      if (event.message?.includes('Channel detached') ||
          event.error?.message?.includes('Channel detached') ||
          event.reason?.message?.includes('Channel detached')) {
        // Prevent the error from showing in React error overlay
        event.preventDefault();
        console.debug('Suppressed Ably channel detached error (safe to ignore)');
        return true;
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', (event) => {
      if (event.reason?.message?.includes('Channel detached') ||
          event.reason?.message?.includes('detached')) {
        event.preventDefault();
        console.debug('Suppressed Ably unhandled rejection (safe to ignore)');
      }
    });

    return () => {
      window.removeEventListener('error', handleError);
    };
  }, []);

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
      if (Notification.permission === 'default') {
        // Don't auto-request, let user trigger it
      }
    }
  }, []);

  // Activity tracking for auto-away
  useEffect(() => {
    const AWAY_TIMEOUT = 5 * 60 * 1000; // 5 minutes

    const resetActivity = () => {
      lastActivityRef.current = Date.now();

      // If we were away, come back online
      if (myPresenceStatus === 'away') {
        setMyPresenceStatus('online');
      }

      // Reset the away timeout
      if (awayTimeoutRef.current) {
        clearTimeout(awayTimeoutRef.current);
      }

      awayTimeoutRef.current = setTimeout(() => {
        // Only go away if not DND
        if (myPresenceStatus !== 'dnd') {
          setMyPresenceStatus('away');
        }
      }, AWAY_TIMEOUT);
    };

    // Track user activity
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(event => {
      window.addEventListener(event, resetActivity, { passive: true });
    });

    // Also track visibility changes
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // User switched tabs - start away timer
        awayTimeoutRef.current = setTimeout(() => {
          if (myPresenceStatus !== 'dnd') {
            setMyPresenceStatus('away');
          }
        }, AWAY_TIMEOUT);
      } else {
        // User came back
        resetActivity();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Initial activity reset
    resetActivity();

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, resetActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (awayTimeoutRef.current) {
        clearTimeout(awayTimeoutRef.current);
      }
    };
  }, [myPresenceStatus]);

  // Global presence - track all online users across the platform
  useEffect(() => {
    if (!client || !currentUser || !isConnected) {
      return;
    }

    // Global presence channel for the entire tenant
    const globalChannelName = `${tenant}:presence:global`;
    const globalPresenceChannel = client.channels.get(globalChannelName);
    globalPresenceChannelRef.current = globalPresenceChannel;

    // Suppress "Channel detached" errors on the presence channel
    globalPresenceChannel.on('error', (err) => {
      if (err?.message?.includes('detached') || err?.code === 90001) return;
      console.warn('Global presence channel error:', err);
    });

    // Presence data to share - use 'online' as initial status
    // Status updates are handled by the separate useEffect below
    const presenceData = {
      status: 'online',  // Initial status, updates happen via separate effect
      userType: currentUser.type,
      userId: currentUser.id,
      displayName: currentUser.displayName || currentUser.email,
    };

    // IMPORTANT: Order matters to avoid race conditions!
    // 1. Subscribe to events FIRST so we don't miss any
    // 2. Enter presence SECOND
    // 3. Get initial members LAST and MERGE (don't replace)

    // Subscribe to presence events FIRST
    const handleEnter = (member) => {
      if (member.data) {
        const key = `${member.data.userType}:${member.data.userId}`;
        setGlobalPresence(prev => ({
          ...prev,
          [key]: { ...member.data, clientId: member.clientId }
        }));
      }
    };

    const handleLeave = (member) => {
      if (member.data) {
        const key = `${member.data.userType}:${member.data.userId}`;
        setGlobalPresence(prev => {
          const updated = { ...prev };
          delete updated[key];
          return updated;
        });
      }
    };

    const handleUpdate = (member) => {
      if (member.data) {
        const key = `${member.data.userType}:${member.data.userId}`;
        setGlobalPresence(prev => ({
          ...prev,
          [key]: { ...member.data, clientId: member.clientId }
        }));
      }
    };

    globalPresenceChannel.presence.subscribe('enter', handleEnter);
    globalPresenceChannel.presence.subscribe('leave', handleLeave);
    globalPresenceChannel.presence.subscribe('update', handleUpdate);

    // Enter global presence SECOND (after subscriptions are set up)
    globalPresenceChannel.presence.enter(presenceData).then(() => {
      // Get initial presence members LAST and MERGE into existing state
      // This ensures we don't overwrite any 'enter' events that fired during setup
      globalPresenceChannel.presence.get((err, members) => {
        if (!err && members) {
          setGlobalPresence(prev => {
            const merged = { ...prev };
            members.forEach(member => {
              if (member.data) {
                const key = `${member.data.userType}:${member.data.userId}`;
                merged[key] = {
                  ...member.data,
                  clientId: member.clientId
                };
              }
            });
            return merged;
          });
        }
      });
    }).catch(err => {
      console.error('Failed to enter global presence:', err);
    });

    return () => {
      globalPresenceChannel.presence.unsubscribe('enter', handleEnter);
      globalPresenceChannel.presence.unsubscribe('leave', handleLeave);
      globalPresenceChannel.presence.unsubscribe('update', handleUpdate);
      globalPresenceChannel.presence.leave().catch(() => {});
      globalPresenceChannelRef.current = null;
    };
  }, [client, currentUser, isConnected, tenant]);  // Note: myPresenceStatus not included - updates handled separately

  // Update global presence when status changes
  useEffect(() => {
    if (globalPresenceChannelRef.current && currentUser) {
      globalPresenceChannelRef.current.presence.update({
        status: myPresenceStatus,
        userType: currentUser.type,
        userId: currentUser.id,
        displayName: currentUser.displayName || currentUser.email,
      }).catch(() => {});
    }
  }, [myPresenceStatus, currentUser]);

  // Refresh presence when chat panel opens
  useEffect(() => {
    if (isOpen && globalPresenceChannelRef.current) {
      // Re-fetch presence list to catch any missed events - MERGE don't replace
      globalPresenceChannelRef.current.presence.get((err, members) => {
        if (!err && members) {
          setGlobalPresence(prev => {
            const merged = { ...prev };
            members.forEach(member => {
              if (member.data) {
                const key = `${member.data.userType}:${member.data.userId}`;
                merged[key] = {
                  ...member.data,
                  clientId: member.clientId
                };
              }
            });
            return merged;
          });
        }
      });
    }
  }, [isOpen]);

  // Request notification permission
  const requestNotificationPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      console.log('Browser does not support notifications');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      return permission === 'granted';
    } catch (err) {
      console.error('Error requesting notification permission:', err);
      return false;
    }
  }, []);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    if (soundEnabled && notificationAudioRef.current) {
      notificationAudioRef.current.currentTime = 0;
      notificationAudioRef.current.play().catch(() => {
        // Ignore autoplay errors
      });
    }
  }, [soundEnabled]);

  // Show browser notification
  const showNotification = useCallback((title, body, channelId) => {
    if (!notificationsEnabled) return;
    if (notificationPermission !== 'granted') return;
    if (!('Notification' in window)) return;

    try {
      const notification = new Notification(title, {
        body,
        icon: '/logo192.png', // High-res icon for crisp notifications
        tag: `chat-${channelId}`, // Prevents duplicate notifications for same channel
        requireInteraction: false,
        silent: true // We handle sound ourselves
      });

      // Play our notification sound
      playNotificationSound();

      // Click to open chat and switch to that channel
      notification.onclick = () => {
        window.focus();
        setIsOpen(true);
        setIsMinimized(false);
        // Find and switch to the channel using ref to avoid circular dependency
        const channel = channels.joined?.find(ch => ch.id === channelId);
        if (channel && switchChannelRef.current) {
          switchChannelRef.current(channel);
        }
        notification.close();
      };

      // Auto-close after 5 seconds
      setTimeout(() => notification.close(), 5000);
    } catch (err) {
      console.error('Error showing notification:', err);
    }
  }, [notificationsEnabled, notificationPermission, playNotificationSound, channels.joined]);

  // Extract user info from token
  useEffect(() => {
    const token = getAnyValidToken();
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCurrentUser({
          type: payload.role,
          id: payload.student_id || payload.user_id || payload.alumni_id || payload.admin_id || payload.admin_user_id,
          email: payload.sub
        });
      } catch (err) {
        console.error('Error parsing token:', err);
      }
    }
  }, []);

  // Fetch channels from API
  const fetchChannels = useCallback(async () => {
    const token = getAnyValidToken();
    if (!token) return;

    setLoadingChannels(true);
    try {
      const response = await fetch(`${API_URL}/api/chat/channels?tenant=${tenant}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setChannels(data);

        // Initialize unread counts
        const counts = {};
        data.joined.forEach(ch => {
          counts[ch.id] = ch.unread_count || 0;
        });
        setUnreadCounts(counts);
        setTotalUnread(Object.values(counts).reduce((a, b) => a + b, 0));
      }
    } catch (err) {
      console.error('Error fetching channels:', err);
    } finally {
      setLoadingChannels(false);
    }
  }, [tenant]);

  // Fetch messages for a channel
  const fetchMessages = useCallback(async (channelId, before = null, skipCache = false) => {
    const token = getAnyValidToken();
    if (!token) return;

    // Check cache first for initial load (not "load more")
    if (!before && !skipCache && messageCacheRef.current[channelId]?.length > 0) {
      // Use cached messages immediately
      setMessages(messageCacheRef.current[channelId]);
      setHasMoreMessages(hasMoreCacheRef.current[channelId] ?? false);
      // Fetch fresh data in background to update cache
      fetchMessages(channelId, null, true);
      return;
    }

    // Only show loading if we don't have cached messages
    if (!messageCacheRef.current[channelId]?.length) {
      setLoadingMessages(true);
    }

    try {
      let url = `${API_URL}/api/chat/channels/${channelId}/messages?limit=50`;
      if (before) {
        url += `&before=${before}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (before) {
          // Prepend older messages
          setMessages(prev => {
            const newMessages = [...data.messages, ...prev];
            messageCacheRef.current[channelId] = newMessages;
            return newMessages;
          });
        } else {
          // Replace messages and update cache
          setMessages(data.messages);
          messageCacheRef.current[channelId] = data.messages;
        }
        setHasMoreMessages(data.has_more);
        hasMoreCacheRef.current[channelId] = data.has_more;
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  // Join a channel
  const joinChannel = useCallback(async (channelId) => {
    const token = getAnyValidToken();
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/chat/channels/${channelId}/join`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        await fetchChannels(); // Refresh channel list
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error joining channel:', err);
      return false;
    }
  }, [fetchChannels]);

  // Start a direct message with another user
  const startDM = useCallback(async (targetUserType, targetUserId, targetDisplayName) => {
    const token = getAnyValidToken();
    if (!token) return null;

    try {
      const response = await fetch(`${API_URL}/api/chat/dm/start?tenant=${tenant}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          target_user_type: targetUserType,
          target_user_id: targetUserId
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Refresh channels to get the new DM
        await fetchChannels();

        // Find and switch to the new DM channel
        // We need to wait for channels to refresh, so we return the channel info
        return {
          channelId: data.channel_id,
          ablyChannelName: data.ably_channel_name,
          created: data.created,
          displayName: targetDisplayName
        };
      }
      return null;
    } catch (err) {
      console.error('Error starting DM:', err);
      return null;
    }
  }, [tenant, fetchChannels]);

  // Leave a channel
  const leaveChannel = useCallback(async (channelId) => {
    const token = getAnyValidToken();
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/chat/channels/${channelId}/leave`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Clear cache for this channel
        delete messageCacheRef.current[channelId];
        delete hasMoreCacheRef.current[channelId];

        await fetchChannels(); // Refresh channel list
        if (currentChannel?.id === channelId) {
          setCurrentChannel(null);
          setMessages([]);
        }
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error leaving channel:', err);
      return false;
    }
  }, [fetchChannels, currentChannel]);

  // ============================================
  // CHANNEL MANAGEMENT FUNCTIONS
  // ============================================

  // Create a new channel
  const createChannel = useCallback(async (channelData) => {
    const token = getAnyValidToken();
    if (!token) return null;

    try {
      const response = await fetch(`${API_URL}/api/chat/channels?tenant=${tenant}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(channelData)
      });

      if (response.ok) {
        const data = await response.json();
        await fetchChannels(); // Refresh channel list
        return data;
      }
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create channel');
    } catch (err) {
      console.error('Error creating channel:', err);
      throw err;
    }
  }, [tenant, fetchChannels]);

  // Browse public channels
  const browseChannels = useCallback(async () => {
    const token = getAnyValidToken();
    if (!token) return [];

    try {
      const response = await fetch(`${API_URL}/api/chat/channels/browse?tenant=${tenant}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        return data.channels || [];
      }
      return [];
    } catch (err) {
      console.error('Error browsing channels:', err);
      return [];
    }
  }, [tenant]);

  // Update channel settings
  const updateChannel = useCallback(async (channelId, updateData) => {
    const token = getAnyValidToken();
    if (!token) return null;

    try {
      const response = await fetch(`${API_URL}/api/chat/channels/${channelId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        const data = await response.json();
        await fetchChannels(); // Refresh channel list
        // Update current channel if it's the one being edited
        if (currentChannel?.id === channelId) {
          setCurrentChannel(prev => ({
            ...prev,
            display_name: data.display_name,
            description: data.description,
            visibility: data.visibility,
            icon: data.icon
          }));
        }
        return data;
      }
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update channel');
    } catch (err) {
      console.error('Error updating channel:', err);
      throw err;
    }
  }, [fetchChannels, currentChannel]);

  // Preview audience change impact (platform admin only)
  const previewAudienceChange = useCallback(async (channelId, newAudience) => {
    const token = getAnyValidToken();
    if (!token) return null;

    try {
      const response = await fetch(
        `${API_URL}/api/chat/channels/${channelId}/audience-impact?new_audience=${encodeURIComponent(newAudience)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        return await response.json();
      }
      const error = await response.json();
      throw new Error(error.detail || 'Failed to preview audience change');
    } catch (err) {
      console.error('Error previewing audience change:', err);
      throw err;
    }
  }, []);

  // Archive channel
  const archiveChannel = useCallback(async (channelId) => {
    const token = getAnyValidToken();
    if (!token) return false;

    try {
      const response = await fetch(`${API_URL}/api/chat/channels/${channelId}/archive`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await fetchChannels();
        if (currentChannel?.id === channelId) {
          setCurrentChannel(null);
          setMessages([]);
        }
        return true;
      }
      const error = await response.json();
      throw new Error(error.detail || 'Failed to archive channel');
    } catch (err) {
      console.error('Error archiving channel:', err);
      throw err;
    }
  }, [fetchChannels, currentChannel]);

  // Restore archived channel
  const restoreChannel = useCallback(async (channelId) => {
    const token = getAnyValidToken();
    if (!token) return false;

    try {
      const response = await fetch(`${API_URL}/api/chat/channels/${channelId}/restore`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await fetchChannels();
        return true;
      }
      const error = await response.json();
      throw new Error(error.detail || 'Failed to restore channel');
    } catch (err) {
      console.error('Error restoring channel:', err);
      throw err;
    }
  }, [fetchChannels]);

  // Get archived channels
  const getArchivedChannels = useCallback(async () => {
    const token = getAnyValidToken();
    if (!token) return [];

    try {
      const response = await fetch(`${API_URL}/api/chat/channels/archived?tenant=${tenant}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        return data.channels || [];
      }
      return [];
    } catch (err) {
      console.error('Error fetching archived channels:', err);
      return [];
    }
  }, [tenant]);

  // Get channel members
  const getChannelMembers = useCallback(async (channelId) => {
    const token = getAnyValidToken();
    if (!token) return null;

    try {
      const response = await fetch(`${API_URL}/api/chat/channels/${channelId}/members`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        return await response.json();
      }
      return null;
    } catch (err) {
      console.error('Error fetching channel members:', err);
      return null;
    }
  }, []);

  // Add members to channel
  const addChannelMembers = useCallback(async (channelId, members) => {
    const token = getAnyValidToken();
    if (!token) return null;

    try {
      const response = await fetch(`${API_URL}/api/chat/channels/${channelId}/members`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ members })
      });

      if (response.ok) {
        return await response.json();
      }
      const error = await response.json();
      throw new Error(error.detail || 'Failed to add members');
    } catch (err) {
      console.error('Error adding members:', err);
      throw err;
    }
  }, []);

  // Remove member from channel
  const removeChannelMember = useCallback(async (channelId, userType, userId) => {
    const token = getAnyValidToken();
    if (!token) return false;

    try {
      const response = await fetch(`${API_URL}/api/chat/channels/${channelId}/members/${userType}/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        return true;
      }
      const error = await response.json();
      throw new Error(error.detail || 'Failed to remove member');
    } catch (err) {
      console.error('Error removing member:', err);
      throw err;
    }
  }, []);

  // Update member role
  const updateMemberRole = useCallback(async (channelId, userType, userId, newRole) => {
    const token = getAnyValidToken();
    if (!token) return null;

    try {
      const response = await fetch(`${API_URL}/api/chat/channels/${channelId}/members/${userType}/${userId}/role`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ role: newRole })
      });

      if (response.ok) {
        return await response.json();
      }
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update role');
    } catch (err) {
      console.error('Error updating role:', err);
      throw err;
    }
  }, []);

  // Switch to a channel
  const switchChannel = useCallback(async (channel) => {
    setCurrentChannel(channel);
    setTypingUsers([]);

    if (channel) {
      // Check if we have cached messages for instant display
      const cachedMessages = messageCacheRef.current[channel.id];
      if (cachedMessages?.length > 0) {
        // Show cached messages immediately
        setMessages(cachedMessages);
        setHasMoreMessages(hasMoreCacheRef.current[channel.id] ?? false);
      } else {
        // No cache, clear and fetch
        setMessages([]);
      }

      // Fetch messages (will use cache or fetch fresh)
      await fetchMessages(channel.id);

      // Mark as read
      const token = getAnyValidToken();
      if (token) {
        try {
          await fetch(`${API_URL}/api/chat/channels/${channel.id}/mark-read`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ last_message_id: null })
          });

          // Update unread counts
          setUnreadCounts(prev => ({ ...prev, [channel.id]: 0 }));
          setTotalUnread(prev => Math.max(0, prev - (unreadCounts[channel.id] || 0)));
        } catch (err) {
          console.error('Error marking channel as read:', err);
        }
      }
    }
  }, [fetchMessages, unreadCounts]);

  // Keep switchChannel ref updated for notifications
  useEffect(() => {
    switchChannelRef.current = switchChannel;
  }, [switchChannel]);

  // Send a message
  const sendMessage = useCallback(async (content, contentType = 'text', attachments = [], parentMessageId = null, mentions = []) => {
    const token = getAnyValidToken();
    if (!token || !currentChannel) return null;

    try {
      const response = await fetch(`${API_URL}/api/chat/channels/${currentChannel.id}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          content,
          content_type: contentType,
          attachments,
          parent_message_id: parentMessageId,
          mentions
        })
      });

      if (response.ok) {
        const newMessage = await response.json();
        // Don't add to local state - let it come through Ably to avoid duplicates
        // The message will appear via the Ably subscription
        // Thread replies are handled by the Ably message handler which will:
        // 1. Update the parent's thread_reply_count
        // 2. Add the reply to the threadReplies cache
        return newMessage;
      }
      return null;
    } catch (err) {
      console.error('Error sending message:', err);
      return null;
    }
  }, [currentChannel]);

  // Upload file for chat attachment
  const uploadChatFile = useCallback(async (file) => {
    const token = getAnyValidToken();
    if (!token || !currentChannel) return null;

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('channel_id', currentChannel.id);

      const response = await fetch(`${API_URL}/api/chat/upload-file`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        return await response.json();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      throw err;
    }
  }, [currentChannel]);

  // Fetch thread replies for a message
  const fetchThreadReplies = useCallback(async (messageId, loadMore = false) => {
    const token = getAnyValidToken();
    if (!token) return null;

    // Set loading state
    setThreadReplies(prev => ({
      ...prev,
      [messageId]: {
        ...prev[messageId],
        loading: true
      }
    }));

    try {
      const existingReplies = threadReplies[messageId]?.replies || [];
      const beforeId = loadMore && existingReplies.length > 0
        ? existingReplies[existingReplies.length - 1].id
        : null;

      const url = beforeId
        ? `${API_URL}/api/chat/messages/${messageId}/replies?before=${beforeId}`
        : `${API_URL}/api/chat/messages/${messageId}/replies`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setThreadReplies(prev => ({
          ...prev,
          [messageId]: {
            replies: loadMore ? [...existingReplies, ...data.replies] : data.replies,
            hasMore: data.has_more,
            totalReplies: data.total_replies,
            loading: false
          }
        }));
        return data;
      }
      return null;
    } catch (err) {
      console.error('Error fetching thread replies:', err);
      setThreadReplies(prev => ({
        ...prev,
        [messageId]: {
          ...prev[messageId],
          loading: false
        }
      }));
      return null;
    }
  }, [threadReplies]);

  // Clear reply state (when message is sent or cancelled)
  const clearReplyingTo = useCallback(() => {
    setReplyingTo(null);
  }, []);

  // Search messages in current channel
  const searchMessages = useCallback(async (query, filters = {}, offset = 0) => {
    const token = getAnyValidToken();
    if (!token || !currentChannel || !query.trim()) {
      setSearchResults([]);
      setSearchTotalCount(0);
      return;
    }

    setSearchLoading(true);
    try {
      let url = `${API_URL}/api/chat/channels/${currentChannel.id}/search?q=${encodeURIComponent(query.trim())}&limit=20&offset=${offset}`;

      // Add optional filters
      if (filters.dateFrom) {
        url += `&date_from=${filters.dateFrom}`;
      }
      if (filters.dateTo) {
        url += `&date_to=${filters.dateTo}`;
      }
      if (filters.senderType) {
        url += `&sender_type=${filters.senderType}`;
      }
      if (filters.senderId) {
        url += `&sender_id=${filters.senderId}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (offset === 0) {
          setSearchResults(data.results);
        } else {
          // Append for "load more"
          setSearchResults(prev => [...prev, ...data.results]);
        }
        setSearchTotalCount(data.total_count);
        setSearchHasMore(data.has_more);
        return data;
      }
      return null;
    } catch (err) {
      console.error('Error searching messages:', err);
      return null;
    } finally {
      setSearchLoading(false);
    }
  }, [currentChannel]);

  // Clear search state
  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setSearchResults([]);
    setSearchTotalCount(0);
    setSearchHasMore(false);
    setSearchFilters({ dateFrom: null, dateTo: null, senderType: null, senderId: null });
    setSearchOpen(false);
  }, []);

  // Jump to a specific message (scroll to it and highlight it)
  const jumpToMessage = useCallback(async (messageId) => {
    // First check if the message is in current messages
    const messageInView = messages.find(m => m.id === messageId);

    if (messageInView) {
      // Message is already loaded, just highlight and scroll
      setHighlightedMessageId(messageId);
      setTimeout(() => setHighlightedMessageId(null), 3000);

      // Scroll to the message element
      const messageElement = document.getElementById(`message-${messageId}`);
      if (messageElement) {
        messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    } else {
      // Message not in view - we need to load messages around it
      // For now, just highlight when found after loading more
      // A more advanced implementation would fetch messages around a specific ID
      console.log('Message not in current view. May need to load more messages.');

      // Set highlight anyway - it will show when message is loaded
      setHighlightedMessageId(messageId);
      setTimeout(() => setHighlightedMessageId(null), 5000);
    }

    // Close search panel after jumping
    setSearchOpen(false);
  }, [messages]);

  // Send typing indicator
  const sendTypingIndicator = useCallback(async (isTyping) => {
    const token = getAnyValidToken();
    if (!token || !currentChannel) return;

    try {
      await fetch(`${API_URL}/api/chat/channels/${currentChannel.id}/typing?is_typing=${isTyping}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
    } catch (err) {
      // Silently fail for typing indicators
    }
  }, [currentChannel]);

  // Handle incoming Ably messages
  const handleAblyMessage = useCallback((message) => {
    const { name: eventName, data } = message;

    switch (eventName) {
      case 'message':
        // Check if this is a thread reply
        if (data.parent_message_id) {
          // This is a thread reply - don't add to main message list
          // Instead, update the parent message's thread_reply_count
          setMessages(prev => prev.map(msg =>
            msg.id === data.parent_message_id
              ? { ...msg, thread_reply_count: (msg.thread_reply_count || 0) + 1 }
              : msg
          ));

          // Add to thread replies cache if the thread is currently loaded
          setThreadReplies(prev => {
            if (prev[data.parent_message_id]?.replies) {
              // Check if reply already exists
              const replyExists = prev[data.parent_message_id].replies.some(r => r.id === data.id);
              if (!replyExists) {
                return {
                  ...prev,
                  [data.parent_message_id]: {
                    ...prev[data.parent_message_id],
                    replies: [...prev[data.parent_message_id].replies, data],
                    totalReplies: (prev[data.parent_message_id].totalReplies || 0) + 1
                  }
                };
              }
            }
            return prev;
          });

          // Show thread reply alert (if not from current user)
          const isOwnReply = currentUserRef.current &&
            data.sender?.user_id === currentUserRef.current.id &&
            data.sender?.user_type === currentUserRef.current.type;

          if (!isOwnReply) {
            // Play notification sound
            playNotificationSound();

            // Show toast alert
            setThreadReplyAlert({
              id: Date.now(),
              parentMessageId: data.parent_message_id,
              senderName: data.sender?.display_name || 'Someone',
              content: data.content?.substring(0, 50) + (data.content?.length > 50 ? '...' : '')
            });

            // Highlight the parent message briefly
            setHighlightedMessageId(data.parent_message_id);
            setTimeout(() => setHighlightedMessageId(null), 3000);

            // Auto-dismiss toast after 4 seconds
            setTimeout(() => setThreadReplyAlert(null), 4000);
          }
        } else {
          // Regular message - add to main message list
          const exists = messagesRef.current.some(m => m.id === data.id);
          if (!exists) {
            setMessages(prev => [...prev, data]);
          }
        }
        break;

      case 'typing':
        // Typing events come from the channel we're subscribed to, so no need to check channel_id
        if (data.is_typing) {
          setTypingUsers(prev => {
            const exists = prev.some(u =>
              u.type === data.user.type && u.id === data.user.id
            );
            if (!exists) {
              return [...prev, data.user];
            }
            return prev;
          });

          // Remove typing indicator after 3 seconds
          setTimeout(() => {
            setTypingUsers(prev =>
              prev.filter(u => !(u.type === data.user.type && u.id === data.user.id))
            );
          }, 3000);
        } else {
          setTypingUsers(prev =>
            prev.filter(u => !(u.type === data.user.type && u.id === data.user.id))
          );
        }
        break;

      case 'reaction':
        if (data.message_id) {
          setMessages(prev => prev.map(msg => {
            if (msg.id === data.message_id) {
              const reactions = { ...msg.reactions };
              if (data.action === 'add') {
                if (!reactions[data.emoji]) {
                  reactions[data.emoji] = [];
                }
                reactions[data.emoji].push(data.user);
              } else if (data.action === 'remove') {
                if (reactions[data.emoji]) {
                  reactions[data.emoji] = reactions[data.emoji].filter(
                    u => !(u.user_type === data.user.user_type && u.user_id === data.user.user_id)
                  );
                  if (reactions[data.emoji].length === 0) {
                    delete reactions[data.emoji];
                  }
                }
              }
              return { ...msg, reactions };
            }
            return msg;
          }));
        }
        break;

      case 'message_update':
        if (data.message_id) {
          if (data.type === 'delete') {
            setMessages(prev => prev.filter(m => m.id !== data.message_id));
          } else if (data.type === 'edit') {
            setMessages(prev => prev.map(m =>
              m.id === data.message_id
                ? { ...m, content: data.new_content, is_edited: true }
                : m
            ));
          }
        }
        break;

      case 'read_receipt':
        // Update read_at for messages that were read
        if (data.message_ids && data.read_at) {
          setMessages(prev => prev.map(m =>
            data.message_ids.includes(m.id)
              ? { ...m, read_at: data.read_at }
              : m
          ));
        }
        break;

      case 'pin':
        // Update pinned status for a message
        console.log('Received pin event:', data);
        if (data.message_id) {
          if (data.action === 'pin') {
            setMessages(prev => prev.map(m =>
              m.id === data.message_id
                ? {
                    ...m,
                    is_pinned: true,
                    pinned_by_type: data.pinned_by?.user_type,
                    pinned_by_id: data.pinned_by?.user_id,
                    pinned_at: data.pinned_at
                  }
                : m
            ));
          } else if (data.action === 'unpin') {
            setMessages(prev => prev.map(m =>
              m.id === data.message_id
                ? {
                    ...m,
                    is_pinned: false,
                    pinned_by_type: null,
                    pinned_by_id: null,
                    pinned_at: null
                  }
                : m
            ));
          }
        }
        break;

      case 'channel_updated':
        // Channel settings were updated - refresh channels
        console.log('Channel updated:', data);
        fetchChannels();
        // Update current channel if it's the one that was updated
        if (currentChannel?.id === data.channel_id) {
          setCurrentChannel(prev => ({
            ...prev,
            display_name: data.display_name || prev.display_name,
            description: data.description || prev.description,
            visibility: data.visibility || prev.visibility,
            icon: data.icon || prev.icon
          }));
        }
        break;

      case 'channel_archived':
        // Channel was archived - remove from list
        console.log('Channel archived:', data);
        fetchChannels();
        // If we're in the archived channel, clear it
        if (currentChannel?.id === data.channel_id) {
          setCurrentChannel(null);
          setMessages([]);
        }
        break;

      case 'member_added':
        // New member was added to the channel
        console.log('Member added:', data);
        // Could show a notification or update member list if displayed
        break;

      case 'member_removed':
        // Member was removed from the channel
        console.log('Member removed:', data);
        // Check if current user was removed
        if (currentUser &&
            data.member?.user_type === currentUser.type &&
            data.member?.user_id === currentUser.id) {
          // We were removed - refresh channels and clear current
          fetchChannels();
          if (currentChannel?.id === data.channel_id) {
            setCurrentChannel(null);
            setMessages([]);
          }
        }
        break;

      case 'member_role_changed':
        // Member's role was changed
        console.log('Member role changed:', data);
        // Could update UI if member list is displayed
        break;

      default:
        console.log('Unknown Ably event:', eventName, data);
    }
  }, [fetchChannels, currentChannel, currentUser]);

  // Subscribe to current channel's Ably channel (for message display)
  useChannel(
    client,
    currentChannel?.ably_channel_name,
    handleAblyMessage
  );

  // Subscribe to ALL joined channels for notifications
  useEffect(() => {
    if (!client || !channels.joined?.length) return;

    const currentUserRef = currentUser;

    // Handler for notification messages from any channel
    const handleNotificationMessage = (channelId, channelName, isDM, otherParticipant) => (message) => {
      if (message.name !== 'message') return;

      const data = message.data;

      // Don't notify for own messages
      if (currentUserRef &&
          data.sender?.user_type === currentUserRef.type &&
          data.sender?.user_id === currentUserRef.id) {
        return;
      }

      // Check if current user is mentioned
      const isMentioned = currentUserRef && data.mentions?.some(m =>
        m.user_type === currentUserRef.type && m.user_id === currentUserRef.id
      );

      // Check if we should show notification
      const chatIsClosed = !isOpenRef.current;
      const chatIsMinimized = isMinimizedRef.current;
      const isOtherChannel = currentChannelRef.current?.id !== channelId;

      // For mentions: ALWAYS notify (even if viewing the channel)
      // For regular messages: only notify if chat closed/minimized/other channel
      const shouldNotify = isMentioned || chatIsClosed || chatIsMinimized || isOtherChannel;

      if (shouldNotify) {
        // Update unread count for this channel
        if (chatIsClosed || chatIsMinimized || isOtherChannel) {
          setUnreadCounts(prev => ({
            ...prev,
            [channelId]: (prev[channelId] || 0) + 1
          }));
          setTotalUnread(prev => prev + 1);
        }

        // Show browser notification
        const senderName = data.sender?.display_name || 'Someone';

        // Priority notification for mentions
        if (isMentioned) {
          const title = `${senderName} mentioned you`;
          const body = isDM
            ? data.content
            : `in #${channelName}: ${data.content}`;
          showNotification(title, body, channelId);
        } else {
          // Regular notification
          const title = isDM
            ? senderName
            : `#${channelName}`;
          const body = isDM
            ? data.content
            : `${senderName}: ${data.content}`;
          showNotification(title, body, channelId);
        }
      }
    };

    // Subscribe to each joined channel
    channels.joined.forEach(channel => {
      // Skip if already subscribed
      if (channelSubscriptionsRef.current[channel.id]) return;

      const ablyChannel = client.channels.get(channel.ably_channel_name);
      const handler = handleNotificationMessage(
        channel.id,
        channel.display_name,
        channel.is_dm,
        channel.dm_other_participant
      );

      // Attach error listener to suppress "Channel detached" errors
      const errorHandler = (err) => {
        if (err?.message?.includes('detached') || err?.code === 90001) return;
        console.warn('Channel error on', channel.ably_channel_name, err);
      };
      ablyChannel.on('error', errorHandler);
      ablyChannel.subscribe(handler);
      channelSubscriptionsRef.current[channel.id] = { channel: ablyChannel, handler, errorHandler };
    });

    // Cleanup subscriptions for channels we're no longer in
    Object.keys(channelSubscriptionsRef.current).forEach(channelId => {
      const stillJoined = channels.joined.some(ch => ch.id === parseInt(channelId));
      if (!stillJoined) {
        const sub = channelSubscriptionsRef.current[channelId];
        if (sub) {
          try {
            if (sub.errorHandler) sub.channel.off('error', sub.errorHandler);
            sub.channel.unsubscribe(sub.handler);
          } catch (err) {
            console.debug('Channel unsubscribe warning (safe to ignore):', err.message);
          }
          delete channelSubscriptionsRef.current[channelId];
        }
      }
    });

    // Cleanup all subscriptions on unmount
    return () => {
      Object.values(channelSubscriptionsRef.current).forEach(sub => {
        if (sub) {
          try {
            if (sub.errorHandler) sub.channel.off('error', sub.errorHandler);
            sub.channel.unsubscribe(sub.handler);
          } catch (err) {
            console.debug('Channel unsubscribe warning (safe to ignore):', err.message);
          }
        }
      });
      channelSubscriptionsRef.current = {};
    };
  }, [client, channels.joined, currentUser, showNotification]);

  // Subscribe to user's notification channel for @mentions
  useEffect(() => {
    if (!client || !isConnected || !currentUser) return;

    const notificationChannelName = `notifications:${currentUser.type}:${currentUser.id}`;

    try {
      const notificationChannel = client.channels.get(notificationChannelName);

      const handleNotification = (message) => {
        const data = message.data;

        if (data?.type === 'mention') {
          const mentionData = data.data;
          const title = 'You were mentioned';
          const body = `${mentionData.sender_name} mentioned you: "${mentionData.content_preview}"`;

          // Show browser notification
          showNotification(title, body, mentionData.channel_id);

          // Play notification sound
          if (soundEnabled && notificationAudioRef.current) {
            notificationAudioRef.current.play().catch(() => {});
          }

          // If we have the switchChannel function, we could navigate to that channel
          // This is handled by clicking the notification
        }
      };

      notificationChannel.subscribe('notification', handleNotification);

      return () => {
        try {
          notificationChannel.unsubscribe('notification', handleNotification);
        } catch (err) {
          console.debug('Notification channel unsubscribe warning (safe to ignore):', err.message);
        }
      };
    } catch (err) {
      console.error('Failed to subscribe to notification channel:', err);
    }
  }, [client, isConnected, currentUser, showNotification, soundEnabled]);

  // Load channels on mount
  useEffect(() => {
    if (enabled) {
      fetchChannels();
    }
  }, [enabled, fetchChannels]);

  // Toggle chat open/close
  const toggleChat = useCallback(() => {
    setIsOpen(prev => !prev);
    setIsMinimized(false);
  }, []);

  // Minimize chat
  const minimizeChat = useCallback(() => {
    setIsMinimized(true);
  }, []);

  // Maximize chat
  const maximizeChat = useCallback(() => {
    setIsMinimized(false);
  }, []);

  // Close chat
  const closeChat = useCallback(() => {
    setIsOpen(false);
    setIsMinimized(false);
  }, []);

  // Load more messages
  const loadMoreMessages = useCallback(async () => {
    if (!currentChannel || !hasMoreMessages || loadingMessages) return;

    const oldestMessage = messages[0];
    if (oldestMessage) {
      await fetchMessages(currentChannel.id, oldestMessage.id);
    }
  }, [currentChannel, hasMoreMessages, loadingMessages, messages, fetchMessages]);

  // Add reaction to a message
  const addReaction = useCallback(async (messageId, emoji) => {
    const token = getAnyValidToken();
    if (!token || !currentChannel) return false;

    try {
      const response = await fetch(`${API_URL}/api/chat/messages/${messageId}/reactions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ emoji })
      });

      return response.ok;
    } catch (err) {
      console.error('Error adding reaction:', err);
      return false;
    }
  }, [currentChannel]);

  // Edit a message
  const editMessage = useCallback(async (messageId, newContent) => {
    const token = getAnyValidToken();
    if (!token) return false;

    try {
      const response = await fetch(`${API_URL}/api/chat/messages/${messageId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: newContent })
      });

      return response.ok;
    } catch (err) {
      console.error('Error editing message:', err);
      return false;
    }
  }, []);

  // Delete a message
  const deleteMessage = useCallback(async (messageId) => {
    const token = getAnyValidToken();
    if (!token) return false;

    try {
      const response = await fetch(`${API_URL}/api/chat/messages/${messageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.ok;
    } catch (err) {
      console.error('Error deleting message:', err);
      return false;
    }
  }, []);

  // Pin a message (shared - visible to all channel members)
  const pinMessage = useCallback(async (messageId) => {
    const token = getAnyValidToken();
    if (!token) return false;

    try {
      const response = await fetch(`${API_URL}/api/chat/messages/${messageId}/pin`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.ok;
    } catch (err) {
      console.error('Error pinning message:', err);
      return false;
    }
  }, []);

  // Unpin a message
  const unpinMessage = useCallback(async (messageId) => {
    const token = getAnyValidToken();
    if (!token) return false;

    try {
      const response = await fetch(`${API_URL}/api/chat/messages/${messageId}/pin`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.ok;
    } catch (err) {
      console.error('Error unpinning message:', err);
      return false;
    }
  }, []);

  // Get pinned messages for a channel
  const fetchPinnedMessages = useCallback(async (channelId) => {
    const token = getAnyValidToken();
    if (!token) return null;

    try {
      const response = await fetch(`${API_URL}/api/chat/channels/${channelId}/pinned`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        return await response.json();
      }
      return null;
    } catch (err) {
      console.error('Error fetching pinned messages:', err);
      return null;
    }
  }, []);

  // Get presence status for a specific user (from global presence)
  const getUserPresenceStatus = useCallback((userType, userId) => {
    const key = `${userType}:${userId}`;
    const presence = globalPresence[key];
    return presence?.status || 'offline';
  }, [globalPresence]);

  // Set presence status manually (for DND toggle)
  const setPresenceStatus = useCallback((status) => {
    if (['online', 'away', 'dnd', 'offline'].includes(status)) {
      setMyPresenceStatus(status);
    }
  }, []);

  // Search for users to start a DM with
  const searchUsers = useCallback(async (query) => {
    const token = getAnyValidToken();
    if (!token || !query || query.length < 1) return [];

    try {
      const response = await fetch(
        `${API_URL}/api/chat/users/search?q=${encodeURIComponent(query)}&tenant=${tenant}&limit=10`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        return data.users || [];
      }
      return [];
    } catch (err) {
      console.error('Error searching users:', err);
      return [];
    }
  }, [tenant]);

  // Context value
  const value = {
    // Connection
    client,
    connectionState,
    isConnected,
    connectionError,
    reconnect,

    // UI
    isOpen,
    isMinimized,
    toggleChat,
    minimizeChat,
    maximizeChat,
    closeChat,

    // Channels
    channels,
    currentChannel,
    loadingChannels,
    fetchChannels,
    joinChannel,
    leaveChannel,
    switchChannel,
    startDM,
    searchUsers,

    // Channel Management
    createChannel,
    browseChannels,
    updateChannel,
    previewAudienceChange,
    archiveChannel,
    restoreChannel,
    getArchivedChannels,
    getChannelMembers,
    addChannelMembers,
    removeChannelMember,
    updateMemberRole,

    // Messages
    messages,
    loadingMessages,
    hasMoreMessages,
    sendMessage,
    uploadChatFile,
    loadMoreMessages,
    addReaction,
    editMessage,
    deleteMessage,

    // Pinned Messages
    pinMessage,
    unpinMessage,
    fetchPinnedMessages,

    // Thread Replies
    replyingTo,
    setReplyingTo,
    clearReplyingTo,
    threadReplies,
    fetchThreadReplies,
    threadReplyAlert,
    setThreadReplyAlert,
    highlightedMessageId,

    // Search
    searchQuery,
    setSearchQuery,
    searchResults,
    searchLoading,
    searchFilters,
    setSearchFilters,
    searchOpen,
    setSearchOpen,
    searchTotalCount,
    searchHasMore,
    searchMessages,
    clearSearch,
    jumpToMessage,

    // Typing
    typingUsers,
    sendTypingIndicator,

    // Unread
    unreadCounts,
    totalUnread,

    // User
    currentUser,
    tenant,

    // Notifications
    notificationPermission,
    notificationsEnabled,
    setNotificationsEnabled,
    soundEnabled,
    setSoundEnabled,
    requestNotificationPermission,

    // Presence (global)
    myPresenceStatus,
    setPresenceStatus,
    globalPresence,
    getUserPresenceStatus
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

export default ChatProvider;
